// app/src/lib/cashflow/recurringDetection.js — detect recurring vendor charges (US-3.7).
//
// Strategy:
//   - Group cleared (non-pending), outflow plaidTransactions by normalized merchant name.
//   - Within each group, find subsets of ≥3 occurrences with similar amounts (±10%).
//   - Classify cadence by median day-gap: 7 = weekly, 14 = biweekly,
//     28–32 = monthly, 88–95 = quarterly, 360–370 = annual; else "irregular".
//   - Emit a `recurringCandidate` row per detected stream so the user can confirm.
//   - Re-running is idempotent: candidates dedupe on (userId, merchantNorm, cadence).

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { isoToday } from './util.js';
import { categorizeExpense, getKeywords as getExpenseKeywords } from './expenseKeywords.js';

const MIN_OCCURRENCES = 3;
const AMOUNT_TOLERANCE = 0.10;

/** @typedef {{ candidateId:string, userId:string, merchantNorm:string, merchantName:string, cadence:string, sampleSize:number, avgAmountCents:number, lastSeen:string, nextEstimated:string, status:'PROPOSED'|'CONFIRMED'|'DISMISSED' }} RecurringCandidate */

/**
 * Scan transactions, propose candidates that don't already exist.
 * @returns {Promise<{ proposed: RecurringCandidate[] }>}
 */
export async function scan({ lookbackDays = 180 } = {}) {
  const userId = await requireUserId();
  const cutoff = new Date(); cutoff.setUTCDate(cutoff.getUTCDate() - lookbackDays);
  const cutoffIso = cutoff.toISOString().slice(0, 10);

  const txs = await db.plaidTransactions
    .where('[userId+date]').between([userId, cutoffIso], [userId, isoToday()], true, true)
    .filter((t) => !t.pending && t.amountCents > 0) // outflows only
    .toArray();

  const groups = new Map(); // merchantNorm → tx[]
  for (const t of txs) {
    const k = normalize(t.merchantName);
    if (!k) continue;
    const arr = groups.get(k) || [];
    arr.push(t);
    groups.set(k, arr);
  }

  const existing = await db.recurringCandidates.where('userId').equals(userId).toArray();
  const expenseKw = await getExpenseKeywords();   // operator's utility/maintenance keywords (or defaults)
  const proposed = [];

  for (const [merchantNorm, list] of groups.entries()) {
    if (list.length < MIN_OCCURRENCES) continue;
    list.sort((a, b) => a.date.localeCompare(b.date));
    // Cluster by amount (±10%).
    const clusters = clusterByAmount(list);
    for (const cluster of clusters) {
      if (cluster.length < MIN_OCCURRENCES) continue;
      const gaps = [];
      for (let i = 1; i < cluster.length; i++) {
        gaps.push(daysBetween(cluster[i].date, cluster[i - 1].date));
      }
      const median = medianOf(gaps);
      const cadence = classifyCadence(median);
      if (!cadence) continue;
      const avg = Math.round(cluster.reduce((s, t) => s + t.amountCents, 0) / cluster.length);
      const last = cluster[cluster.length - 1].date;
      const nextEst = projectNext(last, cadence);

      // Skip if a candidate already exists for this (merchant, cadence) pair.
      const dup = existing.find((c) => c.merchantNorm === merchantNorm && c.cadence === cadence);
      if (dup && dup.status !== 'DISMISSED') continue;

      const merchantName = cluster[0].merchantName || merchantNorm;
      const row = {
        candidateId: dup?.candidateId || crypto.randomUUID(),
        userId,
        merchantNorm,
        merchantName,
        cadence,
        sampleSize: cluster.length,
        avgAmountCents: avg,
        lastSeen: last,
        nextEstimated: nextEst,
        // Keyword-suggested expense category (utility / maintenance) from the
        // operator's settings, so the confirm UI can pre-fill it. null = no match.
        suggestedCategory: categorizeExpense(merchantName, '', expenseKw),
        status: 'PROPOSED',
      };
      await db.recurringCandidates.put(row);
      proposed.push(row);
    }
  }
  return { proposed };
}

/** Convert a proposed candidate into a real RecurringItem. */
export async function confirm(candidateId, overrides = {}) {
  const userId = await requireUserId();
  const cand = await db.recurringCandidates.get(candidateId);
  if (!cand || cand.userId !== userId) throw new Error('recurring.confirm: not found');
  if (cand.status === 'CONFIRMED') {
    // Already done — return existing recurring item if we can find one by next date.
    return cand;
  }
  const recurringId = crypto.randomUUID();
  const recurring = {
    recurringId,
    userId,
    label: overrides.label || cand.merchantName,
    vendorName: cand.merchantName,
    // Explicit override wins; else the keyword-suggested category from scan
    // (recomputed here for older candidates that predate suggestedCategory).
    category: overrides.category || cand.suggestedCategory
      || categorizeExpense(cand.merchantName, '', await getExpenseKeywords()) || 'other',
    amountCents: overrides.amountCents != null ? overrides.amountCents : cand.avgAmountCents,
    cadence: cand.cadence,
    nextDate: overrides.nextDate || cand.nextEstimated,
    plaidStreamId: undefined,
    isAutoRenew: overrides.isAutoRenew ?? 1,
    renewalAlertDays: overrides.renewalAlertDays ?? 14,
    seatCount: overrides.seatCount,
  };
  await db.transaction('rw', db.recurringItems, db.recurringCandidates, async () => {
    await db.recurringItems.put(recurring);
    cand.status = 'CONFIRMED';
    await db.recurringCandidates.put(cand);
  });
  return recurring;
}

export async function dismiss(candidateId) {
  const userId = await requireUserId();
  const cand = await db.recurringCandidates.get(candidateId);
  if (!cand || cand.userId !== userId) return;
  cand.status = 'DISMISSED';
  await db.recurringCandidates.put(cand);
}

export async function listProposed() {
  const userId = await requireUserId();
  return db.recurringCandidates.where('[userId+status]').equals([userId, 'PROPOSED']).toArray();
}

// ── helpers ────────────────────────────────────────────────────────────────

function normalize(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }

function daysBetween(a, b) {
  return Math.round((new Date(a + 'T00:00:00Z') - new Date(b + 'T00:00:00Z')) / 86_400_000);
}

function medianOf(nums) {
  if (!nums.length) return 0;
  const sorted = [...nums].sort((a, b) => a - b);
  const m = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[m] : Math.round((sorted[m - 1] + sorted[m]) / 2);
}

function classifyCadence(medianDays) {
  if (medianDays >= 6 && medianDays <= 8)  return 'WEEKLY';
  if (medianDays >= 13 && medianDays <= 15) return 'BIWEEKLY';
  if (medianDays >= 27 && medianDays <= 32) return 'MONTHLY';
  if (medianDays >= 86 && medianDays <= 95) return 'QUARTERLY';
  if (medianDays >= 355 && medianDays <= 375) return 'ANNUAL';
  return null;
}

function clusterByAmount(txs) {
  // Greedy clustering: each tx joins an existing cluster whose median is within
  // ±10%, else starts a new one. Good enough for distinguishing a $200 monthly
  // SaaS from a $5 weekly snack run at the same vendor.
  const clusters = [];
  for (const t of txs) {
    let placed = false;
    for (const c of clusters) {
      const med = c.medianCents || c.items[0].amountCents;
      if (Math.abs(t.amountCents - med) <= med * AMOUNT_TOLERANCE) {
        c.items.push(t);
        c.medianCents = medianOf(c.items.map((x) => x.amountCents));
        placed = true;
        break;
      }
    }
    if (!placed) clusters.push({ items: [t], medianCents: t.amountCents });
  }
  return clusters.map((c) => c.items);
}

function projectNext(lastDate, cadence) {
  const d = new Date(lastDate + 'T00:00:00Z');
  if (cadence === 'WEEKLY') d.setUTCDate(d.getUTCDate() + 7);
  else if (cadence === 'BIWEEKLY') d.setUTCDate(d.getUTCDate() + 14);
  else if (cadence === 'MONTHLY') d.setUTCMonth(d.getUTCMonth() + 1);
  else if (cadence === 'QUARTERLY') d.setUTCMonth(d.getUTCMonth() + 3);
  else if (cadence === 'ANNUAL') d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
}
